import { useState } from 'react';
import { useSelector } from 'react-redux';

import { useAIContext } from 'src/components/Insights/Catalog/AIProvider';
import { useCatalogPageContext } from 'src/components/Insights/Catalog/CatalogPageProvider';
import useDialogState from 'src/components/Insights/utils/dialog';
import catalogSelectors from 'src/redux/catalog/catalog-selectors';
import { assignCoding } from 'src/redux/catalog/catalog-slice';
import { AbstractCode, Code, CodeTag } from 'src/types/insights';
import { createProvider } from 'src/util/provider';

export const [CodebookProvider, useCodebookContext] = createProvider(() => {
  const aiContext = useAIContext();
  const { aiOn } = aiContext;

  const catalogPageContext = useCatalogPageContext();
  const { selectedEntry, dispatch } = catalogPageContext;

  const codebook = useSelector(catalogSelectors.getCodebook);
  const structuralCodes = useSelector(catalogSelectors.getStructuralCodes);
  const thematicCodes = useSelector(catalogSelectors.getThematicCodes);
  const internalCodes = useSelector(catalogSelectors.getInternalCodes);
  const demographicCodes = useSelector(catalogSelectors.getDemographics);

  const [codeFilter, setCodeFilter] = useState('');

  // ••• MENUS & DIALOGS

  const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
  const [dialogState, openDialog, closeDialog] = useDialogState({
    onOpen: () => setMenuAnchor(null),
  });
  const [accordionType, setAccordionType] =
    useState<AccordionType>('demographic');
  const [selectedCode, setSelectedCode] = useState<AbstractCode>();
  const [parentCodeId, setParentCodeId] = useState<number>();

  const openMenu = (
    code: AbstractCode,
    element: Element,
    accordionType: AccordionType
  ) => {
    setParentCodeId(undefined);
    setAccordionType(accordionType);
    setSelectedCode(code);
    setMenuAnchor(element);
  };

  const closeCodeMenu = () => {
    setMenuAnchor(null);
  };

  // ••• CODING

  /** applies the code to `selectedEntry` */
  const applyCode = (codeId: Code['id']) => {
    if (!selectedEntry) return;
    dispatch(assignCoding([codeId, selectedEntry.id]));
  };

  /** Used for prepopulating a parent code when clicking the "Add New Child Code" button  */
  const setNewChildCodeParentId = (selectedCode?: AbstractCode) => {
    setParentCodeId(undefined);
    if (selectedCode?.parent_id) {
      setParentCodeId(selectedCode.parent_id);
    } else if (selectedCode && selectedCode.parent_id === undefined) {
      setParentCodeId(selectedCode.id);
    }
  };

  // ••• AI REASONING

  const [aiReasoningTag, setAIReasoningTag] = useState<CodeTag>();

  const showAIReasoning = (codeId: Code['id']) => {
    if (!aiOn) return;

    const coding = selectedEntry?.merged_codings.find(
      (c) => c.code_id === codeId
    );
    const code = thematicCodes.find((code) => code.id === coding?.code_id);

    if (code && coding?.is_suggested && coding?.ai_metadata?.reason) {
      setAIReasoningTag({
        ...coding,
        ...code,
      });
    }
  };

  const hideAIReasoning = () => setAIReasoningTag(undefined);

  // ••• CONTEXT

  return {
    ...catalogPageContext,
    ...aiContext,

    codebook,
    structuralCodes,
    thematicCodes,
    internalCodes,
    demographicCodes,
    selectedCode,
    applyCode,

    aiReasoningTag,
    showAIReasoning,
    hideAIReasoning,

    codeFilter,
    setCodeFilter,

    accordionType,
    setAccordionType,

    parentCodeId,
    setParentCodeId,
    setNewChildCodeParentId,

    dialogState,
    openDialog,

    menuAnchor,
    openMenu,
    closeCodeMenu,
    closeDialog,
  };
});

// DEFINITIONS

export type AccordionType =
  | 'structural'
  | 'thematic'
  | 'internal'
  | 'demographic';
